import { Diagnostic } from "@codemirror/lint"
import { EditorView } from "@codemirror/view"
import { yamlSchemaLinter } from "@spglobal-pgm/codemirror-json-schema/yaml"
import { parseDocument } from "yaml"

type NodeRange = [number, number, number] | number[]

const parsePathFromMessage = (message: string): (string | number)[] | null => {
  const match = message.match(/ in `([^`]+)`/)
  const rawPath = match?.[1]?.trim()
  if (!rawPath) {
    return null
  }

  const segments = rawPath.split(".").map((segment) => segment.trim())
  const path = segments
    .filter(Boolean)
    .map((segment) => (/^\d+$/.test(segment) ? Number(segment) : segment))

  return path.length ? path : null
}

const getNodeRange = (node: unknown): { from: number; to: number } | null => {
  if (!node || typeof node !== "object") {
    return null
  }

  const range = (node as { range?: NodeRange | null }).range
  if (!Array.isArray(range)) {
    return null
  }

  const from = range[0]
  const to = range[1] ?? range[2]

  if (typeof from !== "number" || typeof to !== "number") {
    return null
  }

  if (to < from) {
    return null
  }

  return { from, to }
}

export const createYamlSchemaLinter = () => {
  const baseLinter = yamlSchemaLinter()

  return (view: EditorView): Diagnostic[] => {
    const diagnostics = baseLinter(view)
    if (diagnostics.length === 0) {
      return diagnostics
    }

    const docText = view.state.doc.toString()
    let yamlDoc: ReturnType<typeof parseDocument> | null = null
    let parseFailed = false

    const getYamlDoc = () => {
      if (parseFailed) {
        return null
      }

      if (!yamlDoc) {
        try {
          yamlDoc = parseDocument(docText)
        } catch {
          parseFailed = true
          return null
        }
      }

      return yamlDoc
    }

    return diagnostics.map((diagnostic) => {
      if (diagnostic.from !== 0 || diagnostic.to !== 0) {
        return diagnostic
      }

      const path = parsePathFromMessage(diagnostic.message)
      if (!path) {
        return diagnostic
      }

      const doc = getYamlDoc()
      if (!doc) {
        return diagnostic
      }

      const node = doc.getIn(path, true)
      const range = getNodeRange(node)
      if (!range) {
        return diagnostic
      }

      return {
        ...diagnostic,
        from: range.from,
        to: range.to,
      }
    })
  }
}
